import { Country } from 'src/api/zrm';
import { getLocale } from 'src/utils/getLocale';

/**
 * Number formatting for the wall screens, written out rather than left to `Intl` so every TV
 * browser renders the same thing: spaces between thousands, a decimal comma, a dash for "no value".
 * The space is non-breaking so a number never wraps across two lines.
 */

const NBSP = ' ';

export const EMPTY = '–';

export const SHORT_MILLION: Record<Country, string> = {
  [Country.SE]: 'mkr',
  [Country.NO]: 'MNOK',
};

const isValue = (value: number | null | undefined): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

/** `1234567` → `1 234 567`, rounded to a whole number. */
export const formatCount = (value: number | null | undefined): string => {
  if (!isValue(value)) return EMPTY;

  const rounded = Math.round(value);
  const digits = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

  return rounded < 0 ? `-${digits}` : digits;
};

/** Full kronor for tables: `4 422 891 kr`. */
export const formatKr = (value: number | null | undefined): string => (
  isValue(value) ? `${formatCount(value)}${NBSP}kr` : EMPTY
);

/** The kronor `formatShortVolume` shows: rounded to 0,1 million from a million up, to whole kronor below. */
export const shownShortVolume = (value: number): number => (
  Math.abs(value) < 1_000_000 ? Math.round(value) : Math.round(value / 100_000) * 100_000
);

/** Tile form: `4,4 MNOK` (`mkr` in Sweden) from a million up, full kronor below. */
export const formatShortVolume = (value: number | null | undefined, country: Country): string => {
  if (!isValue(value)) return EMPTY;

  if (Math.abs(value) < 1_000_000) return formatKr(value);

  return `${(shownShortVolume(value) / 1_000_000).toFixed(1).replace('.', ',')}${NBSP}${SHORT_MILLION[country]}`;
};

/** Dense tables: millions with two decimals (`1 870 000` → `1,87`); the unit goes in the column header. */
export const formatMillions = (value: number | null | undefined): string => (
  isValue(value) ? (value / 1_000_000).toFixed(2).replace('.', ',') : EMPTY
);

/** A 0–1 ratio as `63,1 %`. */
export const formatPercent = (ratio: number | null | undefined): string => (
  isValue(ratio) ? `${(ratio * 100).toFixed(1).replace('.', ',')}${NBSP}%` : EMPTY
);

/** `HH:MM` of an ISO timestamp in the dashboard's timezone (the browser's when none is given). */
export const formatClock = (iso: string, timeZone: string | undefined, country: Country): string => (
  new Date(iso).toLocaleTimeString(getLocale(country), { hour: '2-digit', minute: '2-digit', timeZone })
);

/** `HH:MM:SS` of an epoch ms time in `timeZone`, for the live screens' lists. */
export const formatClockSeconds = (time: number, timeZone: string, country: Country): string => (
  new Date(time).toLocaleTimeString(getLocale(country), { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone })
);

/** `2026-09-28` → `Man` / `Mån`: the short weekday, capitalised, without the Norwegian full stop. */
export const formatWeekday = (date: string, country: Country): string => {
  const weekday = new Date(`${date}T12:00:00Z`)
    .toLocaleDateString(getLocale(country), { weekday: 'short', timeZone: 'UTC' })
    .replace('.', '');

  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
};
